/* Candidate only: Shopify quotes the complete cart. No paid line is removed or retried. */
(() => {
  if (window.K9NativeVolume) return;
  const positive = n => Number.isSafeInteger(n) && n > 0;
  const attributes = properties => Object.entries(properties || {}).filter(([, v]) => v !== '')
    .map(([k, v]) => { if (typeof v !== 'string') throw Error('attributes'); return [k, v]; })
    .sort(([a], [b]) => a.localeCompare(b));
  const identity = item => JSON.stringify([item.id, attributes(item.properties)]);
  const merge = input => {
    const result = new Map();
    for (const item of input) {
      if (!positive(item.id) || !positive(item.quantity) || item.selling_plan_allocation || item.selling_plan) throw Error('line');
      const key = identity(item), line = result.get(key) || { ...item, quantity: 0 };
      line.quantity += item.quantity;
      if (!positive(line.quantity) || attributes(item.properties).length > 250) throw Error('quantity');
      result.set(key, line);
    }
    if (result.size > 250) throw Error('pagination');
    return [...result.values()];
  };
  const quantities = input => new Map(merge(input).map(i => [identity(i), i.quantity]));
  const sameQuantities = (a, b) => {
    const x = quantities(a), y = quantities(b);
    return x.size === y.size && [...x].every(([key, qty]) => y.get(key) === qty);
  };
  const numericId = (id, type) => {
    if (typeof id !== 'string' || !new RegExp(`^gid://shopify/${type}/[0-9]+$`).test(id)) throw Error('id');
    const n = Number(id.split('/').pop()); if (!positive(n)) throw Error('id'); return n;
  };
  const money = (value, currency) => {
    if (value?.currencyCode !== currency || !/^\d+(?:\.\d{1,2})?$/.test(value.amount)) throw Error('money');
    const n = Math.round(Number(value.amount) * 100); if (!Number.isSafeInteger(n)) throw Error('money'); return n;
  };
  const components = line => (line.item_components || []).map(c => {
    if (!positive(c.id) || !positive(c.quantity)) throw Error('components');
    return [c.id, c.quantity];
  }).sort(([a], [b]) => a - b);
  const componentTotals = pairs => {
    const result = new Map();
    for (const [id, quantity] of pairs) { if (!positive(id) || !positive(quantity)) throw Error('components'); result.set(id, (result.get(id) || 0) + quantity); }
    return [...result].sort(([a], [b]) => a - b);
  };
  const benefitBands = fragments => {
    const result = new Map();
    for (const { unit, quantity, allocations, components: nested } of fragments) {
      if (!Number.isSafeInteger(unit) || unit < 0 || !positive(quantity)) throw Error('ambiguous-price');
      const allocated = new Map();
      for (const a of allocations) {
        const key = JSON.stringify([a.type, a.title]);
        allocated.set(key, (allocated.get(key) || 0) + a.amount); // Zero also reserves a promotion's line.
      }
      const perUnit = pairs => pairs.map(([key, amount]) => {
        if (!Number.isSafeInteger(amount) || amount < 0 || amount % quantity) throw Error('ambiguous-price');
        return [key, amount / quantity];
      });
      const key = JSON.stringify([unit, perUnit([...allocated].sort(([a], [b]) => a.localeCompare(b))), perUnit(componentTotals(nested))]);
      result.set(key, (result.get(key) || 0) + quantity);
    }
    return [...result].sort(([a], [b]) => a.localeCompare(b));
  };
  const sameBenefitBands = (expected, items) => {
    try {
      return JSON.stringify(expected.benefitBands) === JSON.stringify(benefitBands(items.map(i => ({ unit: i.final_line_price / i.quantity,
        quantity: i.quantity, allocations: typedAjaxAllocations(expected.allocations, [i]), components: components(i) }))));
    }
    catch { return false; }
  };
  const allComponents = items => componentTotals(items.flatMap(components));
  const codes = cart => [...new Set([
    ...(cart.discount_codes || []).map(c => c.code),
    ...(cart.cart_level_discount_applications || []).filter(d => d.type === 'discount_code').map(d => d.title),
    ...cart.items.flatMap(i => (i.discounts || []).filter(d => d.type === 'discount_code').map(d => d.title)),
    ...cart.items.flatMap(i => (i.line_level_discount_allocations || []).map(a => a.discount_application)
      .filter(d => d?.type === 'discount_code').map(d => d.title)),
  ].map(c => { if (typeof c !== 'string' || !c.trim()) throw Error('code'); return c; }))].sort();
  const validateCart = (cart, currency, allowUnexpanded = false) => {
    if (cart?.currency !== currency || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price)
      || cart.total_price < 0 || cart.items.some(i => !positive(i.product_id) || !i.key
        || !Number.isSafeInteger(i.final_line_price) || i.final_line_price < 0
        || (!allowUnexpanded && Boolean(i.has_components) !== Boolean(i.item_components?.length)))) throw Error('cart');
    merge(cart.items); cart.items.forEach(components); codes(cart); return cart;
  };
  const cartFingerprint = (cart, includeAttributes) => JSON.stringify([cart.currency, cart.total_price,
    cart.items.map(i => [identity(i), i.key, i.product_id, i.quantity, i.original_line_price, i.final_line_price, components(i), fingerprintAllocations([i])])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))), codes(cart),
    (cart.discount_codes || []).map(c => [c.code, c.applicable]).sort(), includeAttributes ? attributes(cart.attributes) : [], cart.note || '']);
  const fingerprint = cart => cartFingerprint(cart, true);
  // Cart attributes may arrive after the displayed quote without changing its prices.
  // Only display -> fresh reference omits them; fresh -> last -> Ajax still preserves all.
  const displayFingerprint = cart => cartFingerprint(cart, false);
  const validateRule = config => {
    if (config.handle !== 'vetements-volumes' || !positive(config.collectionId) || !positive(config.productId)
      || config.currency !== 'EUR' || !/^[A-Z]{2}$/.test(config.country)
      || !Array.isArray(config.tiers) || config.tiers.length !== 2
      || config.tiers.some((t, n) => t.quantity !== n + 2 || t.percentage !== [25, 30][n]
        || typeof t.title !== 'string' || !t.title.trim()) || config.tiers[0].title === config.tiers[1].title) throw Error('rule');
    for (const [key, path] of [['cartUrl', '/cart.js'], ['addUrl', '/cart/add.js'], ['storefrontUrl', '/api/2026-10/graphql.json']]) {
      const url = new URL(config[key], location.origin);
      if (url.origin !== location.origin || url.search || url.hash || !url.pathname.endsWith(path)) throw Error('endpoint');
    }
    return config;
  };
  const expandComponents = (cart, reader) => {
    if (reader?.version !== 1 || reader.currency !== cart.currency || reader.total_price !== cart.total_price || !Array.isArray(reader.items)
      || reader.items.length !== cart.items.length || new Set(reader.items.map(i => i.key)).size !== reader.items.length
      || new Set(cart.items.map(i => i.key)).size !== cart.items.length) throw Error('components-reader');
    return { ...cart, items: cart.items.map(item => {
      const line = reader.items.find(i => i.key === item.key && i.id === item.id && i.quantity === item.quantity);
      if (!line || !Array.isArray(line.components) || Boolean(line.components.length) !== Boolean(item.has_components)) throw Error('components-reader');
      return { ...item, item_components: line.components };
    }) };
  };
  const selectionItems = (catalogue, quantity, choices) => {
    if (![2, 3].includes(quantity)) throw Error('selection');
    return merge(Array.from({ length: quantity }, (_, n) => {
      const variant = catalogue.variants.find(v => String(v.id) === choices[n] && v.available && !v.composed);
      if (!variant) throw Error('unavailable'); return { id: variant.id, quantity: 1 };
    }));
  };
  const parseQuote = (cart, input, config, catalogue, requestedCodes) => {
    if (!cart || cart.lines.pageInfo.hasNextPage || !Array.isArray(cart.discountCodes)
      || cart.discountCodes.map(c => c.code.toLowerCase()).sort().join('\n') !== requestedCodes.map(c => c.toLowerCase()).sort().join('\n')) throw Error('quote');
    const groups = new Map(), actual = [];
    for (const line of cart.lines.nodes) {
      const item = { id: numericId(line.merchandise.id, 'ProductVariant'), quantity: line.quantity,
        product_id: numericId(line.merchandise.product.id, 'Product'), properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])) };
      actual.push(item);
      const key = identity(item), inputItem = input.find(i => identity(i) === key);
      if (!inputItem || (inputItem.product_id && inputItem.product_id !== item.product_id)) throw Error('quote-shape');
      const nested = (line.lineComponents || []).map(c => [numericId(c.merchandise.id, 'ProductVariant'), c.quantity]).sort(([a], [b]) => a - b);
      const subtotal = money(line.cost.subtotalAmount, config.currency), total = money(line.cost.totalAmount, config.currency);
      if (!positive(line.quantity) || subtotal % line.quantity || total % line.quantity) throw Error('ambiguous-price');
      const allocations = line.discountAllocations.map(a => ({ title: a.title || a.code || '', type: a.__typename,
        amount: money(a.discountedAmount, config.currency) }));
      if (allocations.reduce((sum, a) => sum + a.amount, 0) !== subtotal - total) throw Error('allocation');
      const group = groups.get(key) || { ...item, quantity: 0, total: 0, subtotal: 0, allocations: [], components: [], benefitBands: [], unit: total / line.quantity,
        eligible: catalogue.products.includes(item.product_id) && !nested.length && !item.properties._k9_cadeau };
      // New clothing units need an unambiguous price. Existing outside lines may be
      // split by another promotion; retain every price/quantity instead of averaging it away.
      if (group.eligible && group.unit !== total / line.quantity) throw Error('ambiguous-price');
      group.benefitBands.push({ unit: total / line.quantity, quantity: line.quantity, allocations, components: nested });
      group.quantity += line.quantity; group.total += total; group.subtotal += subtotal; group.allocations.push(...allocations);
      group.components.push(...nested); groups.set(key, group);
    }
    if (!sameQuantities(input, actual)) throw Error('quote-shape');
    for (const [key, group] of groups) {
      group.benefitBands = benefitBands(group.benefitBands);
      if (!group.eligible) group.unit = group.total / group.quantity;
      group.components = componentTotals(group.components);
      if (JSON.stringify(group.components) !== JSON.stringify(allComponents(input.filter(i => identity(i) === key)))) throw Error('components');
    }
    const count = [...groups.values()].filter(i => i.eligible).reduce((n, i) => n + i.quantity, 0);
    const tier = [...config.tiers].reverse().find(t => count >= t.quantity);
    for (const line of groups.values()) {
      const volume = line.allocations.filter(a => config.tiers.some(t => t.title === a.title) && a.type === 'CartAutomaticDiscountAllocation');
      if (line.eligible && tier) {
        const netUnit = Math.floor(((line.subtotal / line.quantity) * (100 - tier.percentage) + 50) / 100);
        if (volume.some(a => a.title !== tier.title) || volume.reduce((sum, a) => sum + a.amount, 0) !== line.subtotal - netUnit * line.quantity
          || line.unit !== netUnit) throw Error('native-allocation-missing');
      } else if (volume.some(a => a.amount > 0)) throw Error('unexpected-volume');
    }
    const total = money(cart.cost.totalAmount, config.currency);
    if ([...groups.values()].reduce((n, line) => n + line.total, 0) !== total) throw Error('unallocated-cart-discount');
    return { total, groups, tier };
  };
  const protectedLines = (before, after, config) => {
    for (const [key, line] of before.groups) {
      const next = after.groups.get(key);
      if (!next || next.unit > line.unit || JSON.stringify(next.components) !== JSON.stringify(line.components)) throw Error('benefit-lost');
      if (!line.eligible && JSON.stringify(next.benefitBands) !== JSON.stringify(line.benefitBands)) throw Error('benefit-lost');
      for (const a of allocationTotals(line.allocations.filter(a => !config.tiers.some(t => t.title === a.title)))) {
        if ((allocationTotals(next.allocations).find(b => b[0] === a[0])?.[1] || 0) < a[1]) throw Error('benefit-lost');
      }
    }
  };
  const allocationTotals = allocations => {
    const totals = new Map();
    for (const a of allocations) if (a.amount) { const key = JSON.stringify([a.type, a.title]); totals.set(key, (totals.get(key) || 0) + a.amount); }
    return [...totals].sort(([a], [b]) => a.localeCompare(b));
  };
  const ajaxAllocations = items => allocationTotals(items.flatMap(i => (i.line_level_discount_allocations || []).map(a => {
    if (!Number.isSafeInteger(a.amount) || a.amount < 0) throw Error('allocation');
    const application = a.discount_application;
    return { amount: a.amount, title: application?.title || '',
      type: application?.type === 'automatic' ? 'CartAutomaticDiscountAllocation' : application?.type === 'discount_code'
        ? 'CartCodeDiscountAllocation' : application?.type === '' ? 'AjaxUnspecified' : 'CartCustomDiscountAllocation' };
  })));
  const typedAjaxAllocations = (expected, items) => {
    const amounts = new Map();
    // Keep zero allocations here: they can identify a line reserved by Croq.
    for (const i of items) for (const a of i.line_level_discount_allocations || []) {
      if (!Number.isSafeInteger(a.amount) || a.amount < 0) throw Error('allocation');
      const application = a.discount_application;
      const type = application?.type === 'automatic' ? 'CartAutomaticDiscountAllocation' : application?.type === 'discount_code'
        ? 'CartCodeDiscountAllocation' : application?.type === '' ? 'AjaxUnspecified' : 'CartCustomDiscountAllocation';
      const key = JSON.stringify([type, application?.title || '']);
      amounts.set(key, (amounts.get(key) || 0) + a.amount);
    }
    return [...amounts].map(([key, amount]) => {
      let [type, title] = JSON.parse(key);
      if (type === 'AjaxUnspecified' && expected.some(a => a.type === 'CartAutomaticDiscountAllocation' && a.title === title)) type = 'CartAutomaticDiscountAllocation';
      return { type, title, amount };
    });
  };
  // Zero-valued reservations remain part of both cart snapshots.
  const fingerprintAllocations = items => typedAjaxAllocations([], items)
    .map(a => [JSON.stringify([a.type, a.title]), a.amount]).sort(([a], [b]) => a.localeCompare(b));
  const sameAllocations = (expected, items) => JSON.stringify(allocationTotals(expected)) === JSON.stringify(allocationTotals(typedAjaxAllocations(expected, items)));
  const confirmed = (before, after, added, quoted) => {
    if (!sameQuantities([...before.items, ...added], after.items) || before.currency !== after.currency || after.total_price !== quoted.total
      || JSON.stringify(attributes(before.attributes)) !== JSON.stringify(attributes(after.attributes)) || (before.note || '') !== (after.note || '')
      || JSON.stringify(codes(before)) !== JSON.stringify(codes(after))) return false;
    for (const item of merge(after.items)) {
      const line = quoted.groups.get(identity(item));
      if (!line || line.product_id !== item.product_id || line.total !== after.items.filter(i => identity(i) === identity(item)).reduce((n, i) => n + i.final_line_price, 0)
        || JSON.stringify(line.components) !== JSON.stringify(allComponents(after.items.filter(i => identity(i) === identity(item))))
        || (!line.eligible && !sameBenefitBands(line, after.items.filter(i => identity(i) === identity(item))))
        || !sameAllocations(line.allocations, after.items.filter(i => identity(i) === identity(item)))) return false;
    }
    return true;
  };
  const catalogueQuery = `query K9VolumeCatalogue($product: ID!, $collection: ID!, $country: CountryCode!) @inContext(country: $country) {
    collection(id: $collection) { id products(first: 250) { nodes { id } pageInfo { hasNextPage } } }
    product(id: $product) { id requiresSellingPlan variants(first: 250) { nodes {
      id title availableForSale requiresComponents price { amount currencyCode } image { url(transform: {maxWidth: 160}) altText }
    } pageInfo { hasNextPage } } }
  }`;
  const quoteQuery = `mutation K9VolumeQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: $input) { cart { id discountCodes { code applicable }
      cost { totalAmount { amount currencyCode } } lines(first: 250) { nodes { id quantity attributes { key value }
        merchandise { ... on ProductVariant { id product { id } } }
        ... on ComponentizableCartLine { lineComponents { quantity merchandise { ... on ProductVariant { id } } } }
        cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
        discountAllocations(lineLevelOnly: false) { __typename discountedAmount { amount currencyCode }
          ... on CartAutomaticDiscountAllocation { title } ... on CartCodeDiscountAllocation { code } }
      } pageInfo { hasNextPage } }
    } userErrors { code } warnings { code } }
  }`;
  const clearQuery = `mutation K9VolumeClear($cart: ID!, $lines: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
    cartLinesRemove(cartId: $cart, lineIds: $lines) { cart { totalQuantity } userErrors { code } }
  }`;
  const api = { identity, merge, quantities, sameQuantities, money, codes, components, allComponents, validateCart, fingerprint, displayFingerprint, validateRule, expandComponents,
    selectionItems, parseQuote, protectedLines, confirmed, allocationTotals, ajaxAllocations, sameAllocations,
    catalogueQuery, quoteQuery, clearQuery, pendingQuotes: new Map(), quoteUncertain: false, quoteQueue: Promise.resolve() };
  window.K9NativeVolume = api;
  function register() {
    if (!window.K9NativeOffer || customElements.get('k9-volume-offer')) return;
    const { Base, state } = window.K9NativeOffer;
    const offers = () => [...document.querySelectorAll('k9-patch-offer,k9-product-offer,k9-fixed-offer,k9-gift-offer,k9-bogo-offer,k9-volume-offer')].filter(e => e.config);
    const refresh = () => offers().forEach(e => e.update());
    class VolumeOffer extends Base {
      connectedCallback() {
        this.events?.abort(); this.events = new AbortController(); this.generation = (this.generation || 0) + 1; this.revision = 0;
        this.preparationActive = false;
        try { this.config = validateRule(JSON.parse(this.querySelector('[data-offer-config]').textContent)); } catch { this.hidden = true; return; }
        this.key = `${this.config.handle}:volume:${this.config.productId}`;
        this.draft = state.drafts.get(this.key) || { choices: {}, quantity: 2, blocked: false };
        state.drafts.set(this.key, this.draft); this.verified = null;
        this.button = this.querySelector('[data-submit]'); this.label = this.querySelector('[data-button-label]');
        this.notice = this.draft.message || ''; this.recovery = Boolean(this.draft.blocked);
        const options = { signal: this.events.signal };
        this.addEventListener('change', e => { e.stopPropagation(); this.readSelection(); this.invalidate(); }, options);
        this.addEventListener('input', e => e.stopPropagation(), options);
        this.button.addEventListener('click', () => this.submit(), options);
        const cartAction = 'cart-remove-button,.quantity__button,.quantity__input,button[name="add"],gp-product-button button,.k9-patch-offer__submit';
        document.addEventListener('click', e => { if (!this.contains(e.target) && e.target.closest(cartAction)) this.invalidate(400); }, { ...options, capture: true });
        document.addEventListener('change', e => { if (e.target.matches('.quantity__input')) this.invalidate(400); }, { ...options, capture: true });
        window.addEventListener('focus', () => this.invalidate(), options);
        document.addEventListener('visibilitychange', () => { if (!document.hidden) this.invalidate(); }, options);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidate());
        if (this.draft.blocked) {
          this.hidden = false; this.message(this.notice || this.config.text.uncertain, true);
          for (const control of this.querySelectorAll('input,select')) control.disabled = true;
        }
        const activate = () => { this.preparationActive = true; void this.initialize(); };
        if (window.K9OfferActivation) window.K9OfferActivation(this, activate); else activate();
      }
      disconnectedCallback() { this.events?.abort(); this.unsubscribe?.(); clearTimeout(this.timer); this.generation++; this.revision++; }
      async storefront(query, variables) {
        const { response, data } = await Base.json(this.config.storefrontUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { country: this.config.country, ...variables } }) });
        if (!response.ok || data.errors?.length || !data.data) throw Error('storefront'); return data.data;
      }
      async loadCatalogue() {
        const data = await this.storefront(catalogueQuery, { product: `gid://shopify/Product/${this.config.productId}`, collection: `gid://shopify/Collection/${this.config.collectionId}` });
        if (!data.collection || !data.product || data.collection.products.pageInfo.hasNextPage || data.product.variants.pageInfo.hasNextPage
          || data.product.requiresSellingPlan || numericId(data.product.id, 'Product') !== this.config.productId
          || numericId(data.collection.id, 'Collection') !== this.config.collectionId) throw Error('catalogue');
        const products = data.collection.products.nodes.map(p => numericId(p.id, 'Product'));
        const variants = data.product.variants.nodes.map(v => ({ id: numericId(v.id, 'ProductVariant'), title: v.title,
          price: money(v.price, this.config.currency), available: v.availableForSale, composed: v.requiresComponents, image: v.image?.url }));
        if (!products.includes(this.config.productId) || new Set(products).size !== products.length || !variants.length
          || new Set(variants.map(v => v.id)).size !== variants.length || variants.some(v => !positive(v.price))) throw Error('catalogue');
        return { products, variants };
      }
      async readCart() {
        const initial = await Base.json(this.config.cartUrl); if (!initial.response.ok) throw Error('cart');
        const raw = validateCart(initial.data, this.config.currency, true);
        if (!raw.items.some(i => i.has_components && !i.item_components?.length)) return validateCart(raw, this.config.currency);
        const url = new URL(this.config.componentsUrl, location.origin);
        if (url.origin !== location.origin || !url.pathname.endsWith('/cart') || url.search !== '?view=k9-volume' || url.hash) throw Error('endpoint');
        const result = await Base.json(url.href); if (!result.response.ok) throw Error('components-reader');
        const expanded = validateCart(expandComponents(raw, result.data), this.config.currency);
        const final = await Base.json(this.config.cartUrl); if (!final.response.ok
          || fingerprint(raw) !== fingerprint(validateCart(final.data, this.config.currency, true))) throw Error('cart-changed');
        return expanded;
      }
      async initialize() {
        if (this.preparationActive === false) return;
        const generation = this.generation;
        try {
          const catalogue = await this.loadCatalogue(); if (!this.isConnected || generation !== this.generation) return;
          this.catalogue = catalogue;
          for (const select of this.querySelectorAll('[data-variant]')) {
            select.replaceChildren(...catalogue.variants.map(v => {
              const option = document.createElement('option'); option.value = String(v.id); option.textContent = v.title;
              option.disabled = !v.available || v.composed; return option;
            }));
            const saved = this.draft.choices[select.dataset.variant];
            select.value = catalogue.variants.find(v => String(v.id) === saved && v.available && !v.composed)?.id
              || catalogue.variants.find(v => v.available && !v.composed)?.id || '';
          }
          for (const radio of this.querySelectorAll('[data-quantity]')) radio.checked = Number(radio.value) === this.draft.quantity;
          this.readSelection(); this.hidden = false; this.invalidate();
        } catch {
          if (generation === this.generation) {
            this.hidden = !this.draft.blocked;
            if (this.draft.blocked) this.message(this.notice || this.config.text.uncertain, true);
          }
        }
      }
      readSelection() {
        this.draft.quantity = Number(this.querySelector('[data-quantity]:checked')?.value);
        for (const s of this.querySelectorAll('[data-variant]')) this.draft.choices[s.dataset.variant] = s.value;
      }
      selection() { return JSON.stringify([this.draft.quantity, this.draft.choices]); }
      invalidate(delay = 160, keepNotice = false) {
        if (!keepNotice && !this.draft.blocked) { this.notice = ''; this.recovery = false; this.draft.message = ''; }
        this.revision++; this.verified = null; clearTimeout(this.timer);
        if (!this.catalogue || this.preparationActive === false) return;
        this.update(); this.timer = setTimeout(() => void this.refreshQuote(), delay);
      }
      update() {
        if (!this.catalogue) return;
        for (const unit of this.querySelectorAll('[data-unit]')) {
          unit.hidden = Number(unit.dataset.unit) >= this.draft.quantity;
          const v = this.catalogue.variants.find(v => String(v.id) === this.draft.choices[unit.dataset.unit]);
          const img = unit.querySelector('img');
          if (img) {
            if (!img.hasAttribute('data-fallback-src')) img.dataset.fallbackSrc = img.getAttribute('src') || '';
            const source = v?.image || img.dataset.fallbackSrc;
            img.removeAttribute('srcset'); img.removeAttribute('sizes');
            if (source) img.src = source; else img.removeAttribute('src');
            img.hidden = !source; img.alt = '';
          }
        }
        this.button.disabled = state.pending || !this.verified || this.draft.blocked || Boolean(window.K9NativeGift?.checkoutBlocked);
        this.button.setAttribute('aria-disabled', String(this.button.disabled));
        this.label.textContent = state.pending ? this.config.text.busy : this.config.text.add[this.draft.quantity];
        for (const control of this.querySelectorAll('input,select')) control.disabled = state.pending || this.draft.blocked;
        this.querySelector('[data-prices]').hidden = !this.verified;
        this.showStatus(this.notice || (this.verified ? '' : this.config.text.wait), this.recovery);
      }
      showStatus(text, recovery = false) {
        const status = this.querySelector('[data-status]'); status.textContent = text; status.hidden = !text;
        this.querySelector('[data-cart-link]').hidden = !recovery;
      }
      message(text, recovery = false) {
        this.draft.message = text; this.notice = text; this.recovery = recovery; this.showStatus(text, recovery);
      }
      async quote(input, cart, catalogue) {
        const queued = api.quoteQueue.then(() => this.performQuote(input, cart, catalogue));
        api.quoteQueue = queued.catch(() => {}); return queued;
      }
      async performQuote(input, cart, catalogue) {
        if (api.pendingQuotes.size || api.quoteUncertain) throw Error('quote-cleanup');
        const requestedCodes = codes(cart);
        if (!input.length) return { total: 0, groups: new Map(), tier: null };
        let created, output;
        try {
          // A lost create response cannot be recovered without its cart ID. Stop further
          // quotes in this page session instead of silently accumulating anonymous carts.
          api.quoteUncertain = true;
          const result = (await this.storefront(quoteQuery, { input: { buyerIdentity: { countryCode: this.config.country },
            attributes: attributes(cart.attributes).map(([key, value]) => ({ key, value })), note: cart.note || '', discountCodes: requestedCodes,
            lines: merge(input).map(i => ({ merchandiseId: `gid://shopify/ProductVariant/${i.id}`, quantity: i.quantity,
              attributes: attributes(i.properties).map(([key, value]) => ({ key, value })) })) } })).cartCreate;
          created = result.cart;
          if (created) api.pendingQuotes.set(created.id, created.lines.nodes.map(l => l.id));
          if (created || (created === null && Array.isArray(result.userErrors) && result.userErrors.length)) api.quoteUncertain = false;
          if (!created || result.userErrors.length || result.warnings.some(w => !['DISCOUNT_NO_ENTITLED_LINE_ITEMS', 'DISCOUNT_QUANTITY_NOT_IN_RANGE', 'DISCOUNT_CODE_NOT_HONOURED'].includes(w.code))) throw Error('quote');
          output = parseQuote(created, input, this.config, catalogue, requestedCodes);
        } finally {
          if (created) {
            const cleared = (await this.storefront(clearQuery, { cart: created.id, lines: created.lines.nodes.map(l => l.id) })).cartLinesRemove;
            if (cleared.userErrors.length || cleared.cart?.totalQuantity !== 0) throw Error('quote-cleanup');
            api.pendingQuotes.delete(created.id);
          }
        }
        return output;
      }
      async proposal(cart, catalogue, added) {
        const before = await this.quote(cart.items, cart, catalogue);
        if (before.total !== cart.total_price || cart.items.some(i => {
          const lines = cart.items.filter(j => identity(i) === identity(j)), quoted = before.groups.get(identity(i));
          return quoted?.total !== lines.reduce((n, j) => n + j.final_line_price, 0)
            || (!quoted.eligible && !sameBenefitBands(quoted, lines))
            || !sameAllocations(quoted.allocations, lines);
        })) throw Error('baseline');
        const after = await this.quote([...cart.items, ...added], cart, catalogue);
        protectedLines(before, after, this.config);
        let units = 0;
        for (const item of added) {
          const line = after.groups.get(identity(item)); if (!line?.eligible || !after.tier) throw Error('eligibility');
          const v = catalogue.variants.find(v => v.id === item.id);
          if (line.subtotal / line.quantity !== v.price) throw Error('price-changed');
          units += line.unit * item.quantity;
        }
        return { ...after, units, fingerprint: fingerprint(cart), displayFingerprint: displayFingerprint(cart), selection: this.selection() };
      }
      async refreshQuote() {
        if (this.preparationActive === false || !this.isConnected || this.draft.blocked) return;
        if (state.pending || Base.cartIsBusy()) { this.timer = setTimeout(() => void this.refreshQuote(), 250); return; }
        const revision = ++this.revision, generation = this.generation, version = state.version, selection = this.selection();
        try {
          const cart = await this.readCart(), added = selectionItems(this.catalogue, this.draft.quantity, this.draft.choices);
          const proposal = await this.proposal(cart, this.catalogue, added);
          if (!this.isConnected || revision !== this.revision || generation !== this.generation || selection !== this.selection() || version !== state.version) return;
          this.verified = proposal;
          const format = cents => new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100);
          this.querySelector('[data-total]').textContent = format(proposal.units);
          this.querySelector('[data-cart-total]').textContent = format(proposal.total);
          this.querySelector('[data-cart-price]').hidden = cart.items.length === 0;
          this.querySelector('[data-rate]').textContent = `−${proposal.tier.percentage} %`;
          if (this.notice === this.config.text.beforeError) this.notice = '';
          this.update();
        } catch {
          if (revision === this.revision && generation === this.generation) {
            this.verified = null; this.update(); this.message(this.config.text.beforeError);
          }
        }
      }
      async submit() {
        if (state.pending || this.draft.blocked || !this.verified || Base.cartIsBusy() || window.K9NativeGift?.checkoutBlocked) return;
        const visible = this.verified, selection = this.selection(), generation = this.generation, revision = this.revision;
        state.pending = true; state.version++; refresh(); this.setAttribute('aria-busy', 'true');
        let posted = false, success = false, after;
        try {
          const catalogue = await this.loadCatalogue(), added = selectionItems(catalogue, this.draft.quantity, this.draft.choices);
          const before = await this.readCart(), quote = await this.proposal(before, catalogue, added);
          if (quote.units !== visible.units || quote.total !== visible.total
            || typeof quote.displayFingerprint !== 'string' || quote.displayFingerprint !== visible.displayFingerprint) throw Error('changed');
          const last = await this.readCart(); if (fingerprint(last) !== quote.fingerprint
            || !this.isConnected || this.generation !== generation || this.revision !== revision || this.selection() !== selection || Base.cartIsBusy()
            || window.K9NativeGift?.checkoutBlocked) throw Error('changed');
          posted = true; window.K9NativeGift?.holdMutation(true);
          await Base.json(this.config.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: added }) });
          after = await this.readCart(); success = confirmed(before, after, added, quote);
          if (!success) throw Error('confirmation');
          // Lot 4: anonymous acceptance count (k9-offre-mesure.js), only after full confirmation.
          (window.K9MesureFile ||= []).push({ element: this, total: before.total_price, currency: before.currency });
          this.draft.message = this.config.text.added;
        } catch {
          this.draft.blocked = posted; this.draft.message = this.config.text[posted ? 'uncertain' : 'beforeError'];
          if (posted && !after) { try { after = await this.readCart(); } catch { /* Keep the recovery link; never repeat the add. */ } }
        }
        try {
          const rendered = after && await this.redraw(after, success ? after.items.at(-1)?.id : null);
          const current = offers().find(e => e.key === this.key) || this;
          current.message(this.draft.message, this.draft.blocked || (success && !rendered));
          if (!success) current.querySelector('[data-status]')?.focus();
        } catch { this.message(this.draft.message, true); }
        finally {
          this.verified = null; state.pending = false; state.version++; this.removeAttribute('aria-busy'); refresh();
          if (!this.draft.blocked && this.isConnected) this.invalidate(160, true);
        }
      }
    }
    customElements.define('k9-volume-offer', VolumeOffer);
  }
  register(); document.addEventListener('DOMContentLoaded', register, { once: true });
})();
